import React, { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Badge } from '@/components/ui/badge';
import { ScenarioSelector } from '@/components/roi/ScenarioSelector';
import { FinancialBreakdown } from '@/components/roi/FinancialBreakdown';
import { ExecutiveSummary } from '@/components/roi/ExecutiveSummary';
import { SensitivityAnalysis } from '@/components/roi/SensitivityAnalysis';
import { DecisionInsightPanel } from '@/components/common/DecisionInsightPanel';
import { scenarioData, tournamentCosts } from '@/components/roi/scenarioData';
import { FeedbackWidget } from '@/components/common/FeedbackWidget';
import { trackEvent } from '@/hooks/useAnalytics';

// Page Header Component
const PageHeader = () => (
  <div className="mb-10" data-testid="page-header">
    <div className="flex items-center gap-3 mb-2">
      <Badge className="bg-primary/10 text-primary border-primary/20">CONFIDENTIAL</Badge>
      <Badge className="bg-muted/50 text-muted-foreground border-border/50">2026</Badge>
    </div>
    <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
      2026 World Cup Financial Impact Simulator
    </h1>
    <p className="text-muted-foreground mt-2">
      Scenario-based brand and revenue impact modelling
    </p>
  </div>
);

// Assessment Info Component
const AssessmentInfo = () => (
  <div className="mb-8 p-4 rounded-lg bg-muted/30 border border-border/50">
    <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
      <div>
        <span className="text-muted-foreground">Assessment Date: </span>
        <span className="font-medium text-foreground">
          {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
      </div>
      <div>
        <span className="text-muted-foreground">Model: </span>
        <span className="font-medium text-foreground">Tournament ROI Projection</span>
      </div>
      <div>
        <span className="text-muted-foreground">Classification: </span>
        <span className="font-medium text-foreground">Board-Level Financial Planning</span>
      </div>
    </div>
  </div>
);

// Footer Disclaimer Component
const FooterDisclaimer = () => (
  <div className="mt-10 p-6 rounded-lg bg-muted/20 border border-border/50">
    <h3 className="text-sm font-semibold text-foreground mb-3">Important Disclaimer</h3>
    <p className="text-xs text-muted-foreground leading-relaxed">
      The financial projections, ROI calculations, and scenario analyses presented in this simulator 
      are directional indicators derived from historical tournament data, market benchmarks, and 
      proprietary analytical models. All figures represent strategic planning estimates, not audited 
      financial forecasts. Actual outcomes may vary significantly based on tournament results, market 
      conditions, sponsorship negotiations, and other external factors. This document is intended 
      solely for internal strategic planning and board-level decision-making within the U.S. Soccer 
      Federation. Distribution beyond authorized recipients requires written consent.
    </p>
    <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
      <div className="text-xs text-muted-foreground">
        <span className="font-semibold text-primary">YBV</span> Brand Intelligence Platform
      </div>
      <div className="text-xs text-muted-foreground">
        ISO 10668 Aligned | BSI-Aligned Methodology | Interbrand-Aligned Valuation
      </div>
    </div>
  </div>
);

// Generate decision insight content based on selected scenario
const getDecisionInsight = (scenarioId) => {
  const scenario = scenarioData[scenarioId];
  const totalValue =
    scenario.brandValueIncrease +
    scenario.sponsorshipIncrease +
    scenario.mediaRightsIncrease +
    scenario.ticketRevenue +
    scenario.merchandiseIncrease;
  const netImpact = totalValue - tournamentCosts.total;
  const multiple = totalValue > 0 ? (totalValue / tournamentCosts.total).toFixed(1) : 0;

  const narratives = {
    group_stage: `Under a group-stage exit scenario (${scenario.probability} modeled probability), the estimated net impact is -$${Math.abs(netImpact)}M. As co-host, this outcome represents the primary downside risk, with material implications for 2027+ sponsorship renewal positioning. The host premium floor on ticket and media revenue partially mitigates total value destruction, but brand equity erosion would likely persist into the next commercial cycle.`,
    round_of_32: `A Round of 32 outcome (${scenario.probability} modeled probability) yields an estimated net value of +$${netImpact}M (${multiple}x return on tournament costs). This meets the minimum knockout-stage threshold in the expanded 48-team format but falls below the quarterfinal base-case expectation for a co-host nation. Commercial cycle positioning remains neutral.`,
    round_of_16: `Round of 16 advancement (${scenario.probability} modeled probability) delivers an estimated +$${netImpact}M net impact (${multiple}x return). This baseline outcome meets minimum co-host expectations and provides moderate differentiation from the 2022 cycle. Sponsorship renewal leverage improves incrementally but without material differentiation.`,
    quarterfinal: `The quarterfinal scenario represents the base-case projection (${scenario.probability} modeled probability) with an estimated +$${netImpact}M net value creation (${multiple}x return). This outcome positions the federation favorably for 2027-2030 commercial cycle negotiations and meets market expectations for a co-host program. The return profile supports continued investment in brand development initiatives.`,
    semifinal: `Semifinal achievement (${scenario.probability} modeled probability) would represent a historic outcome, generating an estimated +$${netImpact}M in net value (${multiple}x return). This materially strengthens long-term negotiating leverage and could catalyze a re-rating of the federation's commercial tier. Sponsorship portfolio depth and rate optimization opportunities expand significantly.`,
    champion: `A championship outcome (${scenario.probability} modeled probability) represents a transformational brand event with an estimated +$${netImpact}M net value creation (${multiple}x return). This would fundamentally reposition USMNT as a global Tier-1 property, unlocking commercial benchmarks currently reserved for traditional European and South American soccer powers.`,
  };

  const keyPointsMap = {
    group_stage: [
      'Primary risk to 2027+ sponsorship renewal negotiations',
      'Host premium provides partial revenue floor on ticket and media channels',
      'Brand equity recovery timeline estimated at 2-3 commercial cycles',
    ],
    round_of_32: [
      'Meets minimum knockout-stage threshold in 48-team format',
      'Below base-case market expectations for co-host advancement',
      'Limited differentiation from peer federation benchmarks',
    ],
    round_of_16: [
      'Achieves baseline co-host advancement expectations',
      'Moderate improvement in commercial cycle positioning',
      'Incremental sponsorship leverage without step-change uplift',
    ],
    quarterfinal: [
      'Meets market expectations and base-case projections',
      'Favorable positioning for 2027-2030 commercial negotiations',
      'Supports continued brand development investment thesis',
    ],
    semifinal: [
      'Historic achievement materially reshapes commercial outlook',
      'Significant expansion of sponsorship depth and rate potential',
      'Catalytic effect on domestic soccer market growth projections',
    ],
    champion: [
      'Transformational re-rating of federation commercial tier',
      'Unlocks Tier-1 global sponsorship and media benchmarks',
      'Generational impact on U.S. domestic soccer market trajectory',
    ],
  };

  return {
    title: `${scenario.label} Scenario — Strategic Implications`,
    narrative: narratives[scenarioId],
    keyPoints: keyPointsMap[scenarioId],
  };
};

// Main ROI Simulator Page
export default function ROISimulator() {
  const [selectedScenario, setSelectedScenario] = useState('quarterfinal');
  const insight = getDecisionInsight(selectedScenario);

  const handleScenarioChange = (id) => {
    setSelectedScenario(id);
    trackEvent('scenario_change', { scenario: id });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <PageHeader />
          <AssessmentInfo />
          <ScenarioSelector 
            selectedScenario={selectedScenario} 
            onScenarioChange={handleScenarioChange} 
          />
          <FinancialBreakdown selectedScenario={selectedScenario} />
          <ExecutiveSummary selectedScenario={selectedScenario} />

          <section className="mb-10" data-testid="roi-decision-insight-section">
            <DecisionInsightPanel
              type="decision-insight"
              title={insight.title}
              narrative={insight.narrative}
              keyPoints={insight.keyPoints}
              disclaimer="This insight is generated from modeled scenario assumptions and historical precedent. It represents a directional interpretation for strategic planning, not a certified financial forecast."
            />
          </section>

          <SensitivityAnalysis />
          <FooterDisclaimer />
          <div className="mt-6">
            <FeedbackWidget route="/us-soccer/roi-simulator" />
          </div>
        </div>
      </main>
    </div>
  );
}
